/**
 * Component that displays visible notifications, with the ability to only
 * pay attention to a subset of notification scopes.
 *
 * Its styling can be customized at call site using either props.className or
 * props.classNames: the first to only extend the container's classname with another
 * classname, the second to extend both the container's and the notifications'
 * classnames with a map of classnames (see propTypes for shape).
 *
 * This component accepts children, which will then be used as children of the
 * individual notifications the component displays.
 */

import type * as React from 'react'
import { useEffect, useMemo } from 'react'

import { useCta } from '~publish/hooks/useCta'
import { SEGMENT_NAMES } from '~publish/legacy/constants'

import { type NotificationScopes, NotificationTypes } from '../AppConstants'
import Notification from './Notification'
import type { Notification as NotificationType } from '../values/Notification'

import styles from './css/NotificationContainer.module.css'

const UPGRADE_PATH_NAME = 'queueLimit-upgrade'
const CTA = SEGMENT_NAMES.COMPOSER_QUEUE_LIMIT

interface ClassNames {
  container?: string | null
  notification?: string | null
  successNotification?: string | null
  errorNotification?: string | null
  infoNotification?: string | null
  notificationCloseButton?: string | null
}

export interface NotificationItem {
  id: string
  type: NotificationTypes
  scope: NotificationScopes
  message: string
  showSoftAndHardCloseOptions?: boolean
  cta?: {
    label: string
    action: () => void
    isUpgradePath: boolean
  } | null
}

interface NotificationContainerProps {
  visibleNotifications: NotificationType[]
  scope?: NotificationScopes | string
  notScopes?: NotificationScopes[] | string[]
  type?: string | null
  notTypes?: string[]
  className?: string | null
  classNames?: ClassNames
  style?: React.CSSProperties
  showCloseIcon?: boolean
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  onClose?: (id: string, options?: any) => void
}

const NotificationContainer = (
  args: NotificationContainerProps,
): JSX.Element => {
  const {
    visibleNotifications,
    scope = null,
    notScopes = [],
    type = null,
    notTypes = [],
    className = null,
    classNames = {},
    style = {},
    showCloseIcon = false,
    children,
    onClick,
    onClose,
  } = args

  const cta = useCta(CTA as CTA, {
    upgradePathName: UPGRADE_PATH_NAME,
  })

  const notificationsToDisplay = useMemo(() => {
    let notificationsToDisplay = [...visibleNotifications]

    if (scope !== null) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => notif.scope === scope,
      )
    }

    if (notScopes.length > 0) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => !notScopes.includes(notif.scope),
      )
    }

    if (type !== null) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => notif.type === type,
      )
    }

    if (notTypes.length > 0) {
      notificationsToDisplay = notificationsToDisplay.filter(
        (notif) => !notTypes.includes(notif.type),
      )
    }

    return notificationsToDisplay
  }, [scope, notScopes, type, notTypes, visibleNotifications])

  // Track the upgrade path view for the composer queue limit notification
  // This is done here because the notification is rendered in the composer
  useEffect(() => {
    if (
      scope?.startsWith('PROFILE_QUEUE_LIMIT-') &&
      notificationsToDisplay.length > 0
    ) {
      cta.viewed()
    }
  }, [cta, scope, notificationsToDisplay])

  if (notificationsToDisplay.length === 0) return <></>

  const containerStyles = [
    styles.notificationContainer,
    classNames.container || className,
  ].join(' ')

  const notificationClassNameMap = {
    [NotificationTypes.ERROR]: [
      classNames.notification,
      classNames.errorNotification,
    ].join(' '),

    [NotificationTypes.SUCCESS]: [
      classNames.notification,
      classNames.successNotification,
    ].join(' '),

    [NotificationTypes.INFO]: [
      classNames.notification,
      classNames.infoNotification,
    ].join(' '),
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={containerStyles} onClick={onClick} style={style}>
      {notificationsToDisplay.map((notif) => (
        <Notification
          id={notif.id}
          type={notif.type}
          message={notif.message}
          showCloseIcon={showCloseIcon}
          showSoftAndHardCloseOptions={notif.showSoftAndHardCloseOptions}
          onClose={onClose}
          classNames={{
            notification: notificationClassNameMap[notif.type],
            closeButton: classNames.notificationCloseButton,
          }}
          // @ts-expect-error -- cta does not exist on all Notification types we need to fix types here
          cta={notif.cta}
          key={notif.id}
        >
          {children}
        </Notification>
      ))}
    </div>
  )
}

export default NotificationContainer
